import * as CANNON from 'cannon-es';

// Collision Filter Bitmasks
export const COLLISION_GROUP_GROUND = 1 << 0;       // 1
export const COLLISION_GROUP_CHASSIS = 1 << 1;      // 2
export const COLLISION_GROUP_STATIC = 1 << 2;       // 4
export const COLLISION_GROUP_DYNAMIC_PROP = 1 << 3; // 8
export const COLLISION_GROUP_AI_VEHICLE = 1 << 4;   // 16
export const COLLISION_GROUP_PEDESTRIAN = 1 << 5;   // 32

export class PhysicsWorld {
  public world: CANNON.World;
  public groundMaterial: CANNON.Material;
  public wheelMaterial: CANNON.Material;
  public chassisMaterial: CANNON.Material;
  public obstacleMaterial: CANNON.Material;
  public staticObstacleMaterial: CANNON.Material;
  public dynamicPropMaterial: CANNON.Material;
  public aiVehicleMaterial: CANNON.Material;
  public pedestrianMaterial: CANNON.Material;

  private fixedTimeStep = 1 / 60;
  private maxSubSteps = 3;

  constructor() {
    this.world = new CANNON.World({
      gravity: new CANNON.Vec3(0, -9.81, 0),
    });

    this.world.broadphase = new CANNON.SAPBroadphase(this.world);
    (this.world.solver as CANNON.GSSolver).iterations = 25;
    (this.world.solver as CANNON.GSSolver).tolerance = 0.0005;
    this.world.defaultContactMaterial.friction = 0.4;
    this.world.defaultContactMaterial.restitution = 0.0;

    // Physics materials
    this.groundMaterial = new CANNON.Material('ground');
    this.wheelMaterial = new CANNON.Material('wheel');
    this.chassisMaterial = new CANNON.Material('chassis');
    this.obstacleMaterial = new CANNON.Material('obstacle'); // legacy alias
    this.staticObstacleMaterial = this.obstacleMaterial;
    this.dynamicPropMaterial = new CANNON.Material('dynamicProp');
    this.aiVehicleMaterial = new CANNON.Material('aiVehicle');
    this.pedestrianMaterial = new CANNON.Material('pedestrian');

    // 1. Wheel on Ground (Tire on Asphalt / Curb)
    const wheelGroundContact = new CANNON.ContactMaterial(
      this.wheelMaterial,
      this.groundMaterial,
      {
        friction: 0.95,
        restitution: 0.0,
        contactEquationStiffness: 1e6,
        contactEquationRelaxation: 4,
        frictionEquationStiffness: 1e6,
        frictionEquationRelaxation: 4,
      }
    );
    this.world.addContactMaterial(wheelGroundContact);

    // 2. Chassis on Ground (Underbody scrape)
    const chassisGroundContact = new CANNON.ContactMaterial(
      this.chassisMaterial,
      this.groundMaterial,
      {
        friction: 0.5,
        restitution: 0.1,
      }
    );
    this.world.addContactMaterial(chassisGroundContact);

    // 3. Chassis on Static Obstacle (Buildings, Guardrails, Trees)
    const chassisStaticContact = new CANNON.ContactMaterial(
      this.chassisMaterial,
      this.staticObstacleMaterial,
      {
        friction: 0.45,
        restitution: 0.15, // inelastic crash deformation
        contactEquationStiffness: 1e7,
        contactEquationRelaxation: 3,
      }
    );
    this.world.addContactMaterial(chassisStaticContact);

    // 4. Chassis on Dynamic Prop (Traffic Signs, Bollards)
    const chassisDynamicPropContact = new CANNON.ContactMaterial(
      this.chassisMaterial,
      this.dynamicPropMaterial,
      {
        friction: 0.35,
        restitution: 0.25, // knock & bounce
        contactEquationStiffness: 5e6,
        contactEquationRelaxation: 4,
      }
    );
    this.world.addContactMaterial(chassisDynamicPropContact);

    // 5. Chassis on AI Vehicle (Metal-on-Metal Car Collision)
    const chassisAiVehicleContact = new CANNON.ContactMaterial(
      this.chassisMaterial,
      this.aiVehicleMaterial,
      {
        friction: 0.40,
        restitution: 0.20, // realistic crumple bounce & momentum transfer
        contactEquationStiffness: 8e6,
        contactEquationRelaxation: 3,
      }
    );
    this.world.addContactMaterial(chassisAiVehicleContact);

    // 6. Dynamic Prop on Ground (Signs tumbling down and settling)
    const dynamicPropGroundContact = new CANNON.ContactMaterial(
      this.dynamicPropMaterial,
      this.groundMaterial,
      {
        friction: 0.65,
        restitution: 0.18, // light ground bounce
        contactEquationStiffness: 1e6,
        contactEquationRelaxation: 4,
      }
    );
    this.world.addContactMaterial(dynamicPropGroundContact);

    // 7. AI Vehicle on Ground
    const aiVehicleGroundContact = new CANNON.ContactMaterial(
      this.aiVehicleMaterial,
      this.groundMaterial,
      {
        friction: 0.85,
        restitution: 0.05,
      }
    );
    this.world.addContactMaterial(aiVehicleGroundContact);

    // 8. AI Vehicle on Static Obstacle
    const aiVehicleStaticContact = new CANNON.ContactMaterial(
      this.aiVehicleMaterial,
      this.staticObstacleMaterial,
      {
        friction: 0.50,
        restitution: 0.15,
      }
    );
    this.world.addContactMaterial(aiVehicleStaticContact);

    // 9. Chassis on Pedestrian
    const chassisPedestrianContact = new CANNON.ContactMaterial(
      this.chassisMaterial,
      this.pedestrianMaterial,
      {
        friction: 0.60,
        restitution: 0.05,
        contactEquationStiffness: 3e6,
        contactEquationRelaxation: 4,
      }
    );
    this.world.addContactMaterial(chassisPedestrianContact);

    this.createGroundPlane();
  }

  private createGroundPlane() {
    const groundBody = new CANNON.Body({
      type: CANNON.Body.STATIC,
      shape: new CANNON.Plane(),
      material: this.groundMaterial,
      collisionFilterGroup: COLLISION_GROUP_GROUND,
      collisionFilterMask: -1,
    });
    groundBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    groundBody.updateAABB();
    this.world.addBody(groundBody);
  }


  public step(deltaTime: number) {
    this.world.step(this.fixedTimeStep, deltaTime, this.maxSubSteps);
  }
}

